import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { HTMLAttributes } from 'react'

import { Button } from './button'
import { cn } from './utils'

export interface PaginationProps extends HTMLAttributes<HTMLElement> {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  /** If true, hide the bar when there is only one page. Default false. */
  hideOnSinglePage?: boolean
  /** Total count of items, optional. If provided, can show item ranges. */
  totalItems?: number
  pageSize?: number
  className?: string
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end'

function generatePageItems(current: number, total: number): PageItem[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-end', total]
  }

  if (current >= total - 3) {
    return [1, 'ellipsis-start', total - 4, total - 3, total - 2, total - 1, total]
  }

  return [1, 'ellipsis-start', current - 1, current, current + 1, 'ellipsis-end', total]
}

/**
 * Fixed bottom-center pagination bar for paged lists.
 * Sits comfortably above the queue strip and adheres to the warm paper theme.
 */
export function Pagination({
  page,
  totalPages,
  onPageChange,
  hideOnSinglePage = false,
  totalItems,
  pageSize,
  className,
  ...props
}: PaginationProps) {
  if (totalPages <= 0 || (hideOnSinglePage && totalPages <= 1)) {
    return null
  }

  const safePage = Math.min(Math.max(1, page), totalPages)
  const items = generatePageItems(safePage, totalPages)

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      data-testid="stories-pagination"
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-30',
        'flex items-center gap-1 sm:gap-1.5',
        'rounded-2xl border border-line bg-paper/95 p-1.5 sm:p-2',
        'shadow-pop backdrop-blur supports-[backdrop-filter]:bg-paper/85',
        'max-w-[calc(100vw-2rem)] select-none',
        className,
      )}
      style={{
        bottom: 'calc(var(--shell-bottom, 40px) + 1rem)',
      }}
      {...props}
    >
      <Button
        variant="ghost"
        size="sm"
        className="gap-1 px-2.5 text-ink-2 hover:text-ink disabled:opacity-40"
        disabled={safePage <= 1}
        onClick={() => onPageChange(safePage - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft className="size-4" aria-hidden />
        <span className="hidden sm:inline">Prev</span>
      </Button>

      {/* Compact indicator on mobile (<640px) */}
      <span className="px-2 text-xs font-medium text-ink-2 sm:hidden" aria-live="polite">
        Page {safePage} of {totalPages}
      </span>

      {/* Full numbered buttons on sm: and larger */}
      <div className="hidden items-center gap-1 sm:flex">
        {items.map((item, index) => {
          if (item === 'ellipsis-start' || item === 'ellipsis-end') {
            return (
              <span key={`${item}-${index}`} className="px-1 text-xs text-ink-3" aria-hidden>
                …
              </span>
            )
          }

          const isActive = item === safePage
          return (
            <Button
              key={item}
              variant={isActive ? 'primary' : 'ghost'}
              size="sm"
              className={cn(
                'min-w-9 px-2 text-sm',
                isActive ? 'font-semibold' : 'text-ink-2 hover:text-ink',
              )}
              aria-current={isActive ? 'page' : undefined}
              aria-label={isActive ? `Page ${item}` : `Go to page ${item}`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          )
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="gap-1 px-2.5 text-ink-2 hover:text-ink disabled:opacity-40"
        disabled={safePage >= totalPages}
        onClick={() => onPageChange(safePage + 1)}
        aria-label="Next page"
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRight className="size-4" aria-hidden />
      </Button>

      {totalItems !== undefined && pageSize !== undefined && totalPages > 1 ? (
        <span className="hidden pl-1 pr-2 text-xs text-ink-3 md:inline" aria-hidden>
          {Math.min((safePage - 1) * pageSize + 1, totalItems)}–{Math.min(safePage * pageSize, totalItems)} of {totalItems}
        </span>
      ) : null}
    </nav>
  )
}
